import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { CompanyPipelineDto } from '@lib/dto/pipeline.dto'
import { CompanyTracking } from '@/components/pipeline/CompanyTracking'
import { CompanyVenues } from '@/components/pipeline/CompanyVenues'
import { LostPanel } from '@/components/pipeline/LostPanel'
import { PipelineFunnel } from '@/components/pipeline/PipelineFunnel'
import { PipelineSummary } from '@/components/pipeline/PipelineSummary'
import { PipelineTiles } from '@/components/pipeline/PipelineTiles'
import { companyConnections, PIPELINE_VIEW_LABELS } from '@/components/pipeline/pipelineText'
import { usePipelineQuery } from '@/components/pipeline/usePipelineQuery'
import { InfoNote, PeriodBar, ViewToggle } from '@/components/stats/StatsPieces'
import { SplitFilter } from '@/components/pipeline/SplitFilter'
import { SpanControls } from '@/components/pipeline/SpanControls'
import { PIPELINE_SPAN_PERIODS } from '@lib/enums/pipeline-span.enum'
import { PageContainer } from '@/components/ui/page-container'
import { Checkbox } from '@/components/ui/checkbox'
import { PageHeader } from '@/components/ui/page-header'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCompanyPipeline, useSetPipelineEmail } from '@/hooks/usePipeline'
import { useOrganization } from '@/hooks/useOrganization'
import { companyStoryInput } from '@lib/helpers/pipeline-story'

/**
 * The pipeline email's switch, at a company without GoHighLevel (docs/pipeline-view.spec.md,
 * "The email"). The company has no settings page, so it sits beside what it sends.
 */
function EmailSwitch({ email }: { email: NonNullable<CompanyPipelineDto['email']> }) {
  const setEmail = useSetPipelineEmail()
  const [error, setError] = useState<string | null>(null)
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-sm text-ink">
        <Checkbox
          checked={email.on}
          disabled={!email.canChange || setEmail.isPending}
          onCheckedChange={(on) => {
            setError(null)
            setEmail.mutate({ on }, { onError: (e) => setError(requestErrorMessage(e)) })
          }}
        />
        Email each venue’s team its pipeline, and organization admins the company’s, after each two-week period
      </label>
      {email.canChange ? null : <p className="m-0 text-xs text-ink-muted">An organization admin turns this on and off.</p>}
      {error === null ? null : <FormError size="sm">{error}</FormError>}
    </div>
  )
}

/**
 * The company's pipeline (docs/pipeline-view.spec.md, "The company page"),
 * laid out as a venue's is: the short version, naming the venues that moved the
 * total; the company's headline numbers; its venues ranked by what needs a
 * look; where its couples drop off against what's usual; why they were lost;
 * and what its numbers can't see, venue by venue. Its counts don't open; each
 * venue's page, linked in the same weeks, does.
 */
export function PipelinePage() {
  const organizationQuery = useOrganization()
  const location = useLocation()
  const [query, setQuery] = usePipelineQuery()
  const pipelineQuery = useCompanyPipeline(query)
  const data = pipelineQuery.data

  if (pipelineQuery.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (pipelineQuery.isError || data === undefined) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(pipelineQuery.error)} onRetry={() => void pipelineQuery.refetch()} />
      </PageContainer>
    )
  }

  const window = data.window
  // A venue's own Pipeline, in the same weeks, view and couples as this page.
  const linkTo = (venueId: string): string => `/venues/${venueId}/pipeline${location.search}`
  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="space-y-2">
        <PageHeader title="Pipeline" meta={organizationQuery.data?.name} />
        {data.email === null ? null : <EmailSwitch email={data.email} />}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ViewToggle view={data.view} labels={PIPELINE_VIEW_LABELS} onView={(view) => setQuery({ ...query, view })} />
        <SplitFilter splits={data.splits} split={data.split} onSplit={(split) => setQuery({ ...query, split })} />
        <SpanControls
          span={query.span}
          compare={query.compare}
          onSpan={(span) => setQuery({ ...query, span })}
          onCompare={(compare) => setQuery({ ...query, compare })}
        />
      </div>

      {window === null ? (
        <p className="m-0 text-sm text-ink-muted">No periods yet.</p>
      ) : (
        <>
          <PeriodBar
            window={window}
            periods={data.periods}
            spend={null}
            step={PIPELINE_SPAN_PERIODS[query.span]}
            onPeriod={(period) => setQuery({ ...query, period })}
          />
          <PipelineSummary input={companyStoryInput(data)} />
          <PipelineTiles data={data} whose="company" />
          <CompanyVenues data={data} linkTo={linkTo} />
          <PipelineFunnel data={{ ...data, lookAt: null }} whose="company" />
          <div className="flex flex-wrap items-stretch gap-6">
            <LostPanel reasons={data.lostReasons} view={data.view} />
            <div className="min-w-0 flex-[2_1_30rem]">
              <CompanyTracking rows={data.rows} linkTo={linkTo} companyHas={companyConnections(organizationQuery.data?.features ?? [])} />
            </div>
          </div>
          <InfoNote>
            Couples are added up across venues, and shares, what’s usual and costs worked out from those totals. A venue that doesn’t record a
            step adds nothing to it.
          </InfoNote>
        </>
      )}
    </PageContainer>
  )
}
